import { useState } from 'react'
import styles from './GroupsTableView.module.css'
import { ConfirmationModal } from '../modals'

/**
 * GroupsTableView - Component for displaying groups in a table format with actions
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles group table display and interactions
 * - Open/Closed: Extensible through props without modification
 * - Interface Segregation: Focused interface for group table operations
 */
export default function GroupsTableView({ 
    groups = [], 
    onRemoveGroup, 
    loading = false,
    member = null 
}) {
    const [confirmingRemoval, setConfirmingRemoval] = useState(null)

    const handleRemoveClick = (group) => {
        setConfirmingRemoval(group)
    }

    const handleConfirmRemove = async () => {
        if (!confirmingRemoval || !member) return

        try {
            await onRemoveGroup(confirmingRemoval.id, member.id)
            setConfirmingRemoval(null)
        } catch (error) {
            console.error('Failed to remove member from group:', error)
            // Error handling is delegated to parent component
        }
    }

    const handleCancelRemove = () => {
        setConfirmingRemoval(null)
    }

    const getConfirmationMessage = () => {
        if (!confirmingRemoval || !member) return ''
        
        const memberName = member.full_name || member.display_name || member.email
        const groupName = confirmingRemoval.name
        
        return `Are you sure you want to remove ${memberName} from ${groupName}?`
    }

    if (loading) {
        return (
            <div className={styles.loadingState}>
                <p>Loading groups...</p>
            </div>
        )
    }

    if (groups.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>This member is not part of any groups</p>
            </div>
        )
    }

    return (
        <>
            <div className={styles.tableContainer}>
                <table className={styles.groupsTable}>
                    <thead>
                        <tr>
                            <th>Group Name</th>
                            <th>Description</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <tr key={group.id} className={styles.tableRow}>
                                <td>
                                    <div className={styles.groupInfo}>
                                        <span className={styles.groupName}>{group.name}</span>
                                    </div>
                                </td>
                                <td>
                                    <span className={styles.groupDescription}>
                                        {group.description || 'No description'}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleRemoveClick(group)}
                                            aria-label={`Remove from ${group.name}`}
                                            title={`Remove from ${group.name}`}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmationModal
                isOpen={!!confirmingRemoval}
                onClose={handleCancelRemove}
                onConfirm={handleConfirmRemove}
                title="Remove from Group"
                message={getConfirmationMessage()}
                confirmText="Remove"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            />
        </>
    )
}